import type { LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/Button';
import { logout } from '../features/auth/api';
import { useAuth } from '../app/authContext';

/** Full-screen status message (pending/deactivated): nothing else is reachable. */
export function StatusLayout({
  icon: Icon,
  title,
  body,
  tone,
  children,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
  tone: 'amber' | 'red';
  /** Extra content under the body, e.g. the verify-email note. */
  children?: ReactNode;
}) {
  const { profile } = useAuth();
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-mist px-4 py-10">
      <Logo />
      <main className="w-full max-w-md rounded-card border border-stone bg-white p-6 text-center shadow-card md:p-8">
        <span
          className={`mx-auto flex size-14 items-center justify-center rounded-full ${
            tone === 'amber' ? 'bg-sunrise-50 text-sunrise-700' : 'bg-danger-50 text-danger-700'
          }`}
        >
          <Icon className="size-7" aria-hidden />
        </span>
        <h1 className="mt-4 text-xl font-semibold text-ink">{title}</h1>
        <p className="mt-2 text-sm text-slate">{body}</p>
        {children}
        {profile && (
          <p className="mt-4 text-xs text-slate">
            Signed in as {profile.email} · Unit{' '}
            <span className="tabular-nums">{profile.unitNumber}</span>
          </p>
        )}
        <Button
          variant="secondary"
          className="mt-6"
          loading={busy}
          onClick={() => {
            setBusy(true);
            void logout();
          }}
        >
          Sign out
        </Button>
      </main>
    </div>
  );
}
